import * as React from 'react';
import { connect } from 'react-redux';
import { HashRouter, Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { AuthLevel, ServerService } from '../../../ts/services/ServerService';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServerUrls } from '../../ServerUrls';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { BackgroundJobsSpaceProvider } from '../BackgroundJobsSpace/BackgroundJobsSpaceProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { TableauReactRouterSwitch } from '../RouterHelpers/TableauReactRouterSwitch';
import { ScheduleSpaceRouter } from '../Schedules/ScheduleRouter';
import { AllSchedulesSpaceProvider } from '../Schedules/SchedulesSpaceProvider';
import { ServerUserSpaceProvider } from '../ServerUser/ServerUserSpaceProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { AllSitesSpaceProvider } from './Sites/AllSitesSpaceProvider';

export interface ServerSpaceRouterOwnProps extends ReactDirectiveProps {}

export interface ServerSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
}

export type ServerSpaceRouterProps = ServerSpaceRouterOwnProps & ServerSpaceRouterStateProps;

export class ServerSpaceRouterComponent extends React.PureComponent<ServerSpaceRouterProps> {
  private server: ServerService;

  public constructor(props: ServerSpaceRouterProps) {
    super(props);
    this.server = ServiceLocator.get<ServerService>('ServerService');
  }

  private memoizedGetWatchedBasePaths = defaultMemoize(ServerSpaceRouterComponent.getWatchedBasePaths);
  public render() {
    const basePaths = this.memoizedGetWatchedBasePaths();
    return (
      <div className="tb-fill">
        <HashRouter>
          <TableauReactRouterSwitch basePaths={basePaths} urls={this.props.urls}>
            { this.server.isUserAuthorized(AuthLevel.ServerAdmin) &&
              this.routes()
            }
            <Route render={this.error}/>
          </TableauReactRouterSwitch>
        </HashRouter>
      </div>
    );
  }

  private routes = () => {
    return (
      <Switch>
        <Route path={ServerUrls.getInstance().sitesPath()} render={this.serverSitesSpace} />
        {
          FeatureFlags.jobManagementEnabled &&
          <Route
            path={ServerUrls.getInstance().jobsPath()}
            render={(routeProps) => <BackgroundJobsSpaceProvider
              angularContext={this.props.angularContext}
              routeLocationSearch={routeProps.location.search}
              routeLocationPath={routeProps.location.pathname}
            />}
          />
        }
        <Route
          exact path={ServerUrls.getInstance().schedulesPath()}
          render={(routeProps) => <AllSchedulesSpaceProvider
            angularContext={this.props.angularContext}
            routeLocationSearch={routeProps.location.search}
            routeLocationPath={routeProps.location.pathname}
            urls={ServerUrls.getInstance()}
          />}
        />
        <Route
          path={ServerUrls.getInstance().schedulePath(':id')}
          render={() => <ScheduleSpaceRouter angularContext={this.props.angularContext} urls={ServerUrls.getInstance()} />}
        />
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userSettingsPath(':domainName', ':username'))}
          render={(routeProps) => <ServerUserSpaceProvider
            angularContext={this.props.angularContext}
            domainName={routeProps.match.params.domainName}
            userName={routeProps.match.params.username}
          />}
        />
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userPath(':domainName', ':username'))}
          render={({ match }) => <Redirect to={ServerUrls.getInstance().userSettingsPath(match.params.domainName, match.params.username)}/>}
        />
        <Route render={this.error}/>
      </Switch>
    );
  }

  private error = (): JSX.Element => {
    return <RouterError errorType={'server-default'} urls={this.props.urls}/>;
  }

  private serverSitesSpace = (props: RouteComponentProps): JSX.Element => (
    <AllSitesSpaceProvider
      angularContext={this.props.angularContext}
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  )

  public static getWatchedBasePaths() {
    const serverUrls = ServerUrls.getInstance();
    return _.compact([
      serverUrls.sitesPath(),
      serverUrls.jobsPath(),
      serverUrls.schedulesPath(),
      serverUrls.baseUserPath(),
    ]);
  }
}

const mapStateToProps: MapStateToProps<ServerSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state)
});

export const ServerSpaceRouter: React.ComponentClass<ServerSpaceRouterOwnProps>
  = connect(mapStateToProps)(ServerSpaceRouterComponent);
